import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button } from '@opendate-app/setlist';
import UserManualSidebar from './UserManualSidebar';
import UserManualArticle from './UserManualArticle';
import UserManualSearch from './UserManualSearch';
import UserManualAdminMenu from './UserManualAdminMenu';
import UserManualArticleEditor from './UserManualArticleEditor';
import UserManualDuplicateCompare from './UserManualDuplicateCompare';
import generateHeadingId from '../utils/generateHeadingId';
import humanizeSlug from '../utils/humanizeSlug';
import { buildArticleMap, buildArticleList } from '../utils/articleIndex';
import '../styles/userManualStyles.css';

const getFirstArticleKey = (sections) => {
  const section = sections[0];
  if (!section) return null;
  const chapter = section.chapters[0];
  if (!chapter) return null;
  const article = chapter.articles[0];
  if (!article) return null;
  return `${section.slug}/${chapter.slug}/${article.slug}`;
};

const getArticleKeyFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  return hash || null;
};

const unescapeMarkdown = (text) => text.replace(/\\([!-/:-@[-`{-~])/g, '$1');

const parseHeadings = (markdown) => {
  if (!markdown) return [];
  const headings = [];
  const lines = markdown.split('\n');
  lines.forEach((line) => {
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (match) {
      headings.push({
        level: match[1].length,
        text: unescapeMarkdown(match[2].trim()),
        id: generateHeadingId(match[2].trim()),
      });
    }
  });
  return headings;
};

const OnPageNav = ({ headings }) => {
  if (headings.length === 0) return null;

  return (
    <aside className="user-manual-on-page-nav">
      <div className="user-manual-on-page-nav-title">On this page</div>
      <ul className="user-manual-on-page-nav-list">
        {headings.map((heading) => (
          <li key={heading.id} className={heading.level === 3 ? 'user-manual-on-page-nav-indent' : ''}>
            <a href={`#${heading.id}`} className="user-manual-on-page-nav-link">
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
};

const UserManualPage = ({ sections, adminDraftsEnabled }) => {
  const articleMap = useMemo(() => buildArticleMap(sections), [sections]);
  const articleList = useMemo(() => buildArticleList(sections), [sections]);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [draftView, setDraftView] = useState(null);
  const [draftRefreshToken, setDraftRefreshToken] = useState(0);
  const draftSelectionRequestRef = useRef(0);
  const [unpublishState, setUnpublishState] = useState(null);
  const [duplicateView, setDuplicateView] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [adminDrafts, setAdminDrafts] = useState([]);

  const [activeKey, setActiveKey] = useState(() => {
    const hashKey = getArticleKeyFromHash();
    if (hashKey && articleMap[hashKey]) return hashKey;
    return getFirstArticleKey(sections);
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hashKey = getArticleKeyFromHash();
      if (hashKey && articleMap[hashKey]) {
        setActiveKey(hashKey);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [articleMap]);

  const handleArticleSelect = useCallback((key) => {
    draftSelectionRequestRef.current += 1;
    setDraftView(null);
    setDuplicateView(null);
    setEditingSlug(null);
    setUnpublishState(null);
    setActiveKey(key);
    window.location.hash = key;
    setMobileSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSelectDraft = useCallback((slug, state) => {
    const requestId = draftSelectionRequestRef.current + 1;
    draftSelectionRequestRef.current = requestId;
    setDuplicateView(null);
    setEditingSlug(null);
    setDraftView({ slug, state, loading: true });
    axios
      .get(`/user_manual/drafts/${slug}.json`)
      .then((response) => {
        if (draftSelectionRequestRef.current === requestId) {
          setDraftView({ slug, state, article: response.data });
        }
      })
      .catch(() => {
        if (draftSelectionRequestRef.current === requestId) {
          setDraftView({ slug, state, error: true });
        }
        toast.error("Couldn't load that draft. Try reopening it from the Admin menu.");
      });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePublishDraft = useCallback(() => {
    if (!draftView || !draftView.article) return;
    const confirmed = window.confirm(
      `Publish "${draftView.article.title}"? This goes live to all venues immediately.`,
    );
    if (!confirmed) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const publishedSlug = draftView.slug;
    setDraftView((prev) => (
      prev?.slug === publishedSlug ? { ...prev, publishing: true, publishError: false } : prev
    ));
    axios
      .post('/user_manual/publications.json', { slug: publishedSlug }, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(() => {
        setDraftView((prev) => (
          prev?.slug === publishedSlug
            ? { ...prev, publishing: false, publishError: false, publishedNow: true }
            : prev
        ));
        setDraftRefreshToken((token) => token + 1);
      })
      .catch(() => {
        setDraftView((prev) => (
          prev?.slug === publishedSlug ? { ...prev, publishing: false, publishError: true } : prev
        ));
        toast.error('Publishing failed. Try again, or publish from Conductor.');
      });
  }, [draftView]);

  const handleSelectDuplicate = useCallback((pair) => {
    draftSelectionRequestRef.current += 1;
    setDraftView(null);
    setEditingSlug(null);
    setDuplicateView(pair);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDuplicateResolved = useCallback(() => {
    setDuplicateView(null);
    setDraftRefreshToken((token) => token + 1);
  }, []);

  const handleEditSaved = useCallback((slug, result) => {
    setDraftRefreshToken((token) => token + 1);
    handleSelectDraft(slug, result.published ? 'revision' : 'draft');
  }, [handleSelectDraft]);

  const activeEntry = articleMap[activeKey];

  const handleUnpublishActive = useCallback(() => {
    if (!activeEntry) return;
    const confirmed = window.confirm(
      `Unpublish "${activeEntry.article.title}"? It disappears from the Help Center for all venues and becomes a draft.`,
    );
    if (!confirmed) return;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    setUnpublishState({ working: true });
    axios
      .delete(`/user_manual/publications/${activeEntry.article.slug}.json`, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(() => {
        setUnpublishState({ done: true });
        setDraftRefreshToken((token) => token + 1);
      })
      .catch(() => setUnpublishState({ error: true }));
  }, [activeEntry]);

  const activeIndex = articleList.findIndex((a) => a.key === activeKey);
  const prevArticle = activeIndex > 0 ? articleList[activeIndex - 1] : null;
  const nextArticle = activeIndex < articleList.length - 1 ? articleList[activeIndex + 1] : null;

  const headings = useMemo(() => {
    if (!activeEntry) return [];
    return parseHeadings(activeEntry.article.body);
  }, [activeEntry]);

  return (
    <div className="user-manual-wrapper">
      <div className="user-manual-header">
        <div className="user-manual-header-inner">
          <div className="user-manual-header-title">
            <h1>Help Center</h1>
          </div>
          <div className="user-manual-header-search">
            <UserManualSearch
              sections={sections}
              onSelectArticle={handleArticleSelect}
              drafts={adminDrafts}
              onSelectDraft={handleSelectDraft}
            />
            {adminDraftsEnabled && (
              <UserManualAdminMenu
                onSelectDraft={handleSelectDraft}
                onDraftsLoaded={setAdminDrafts}
                onSelectDuplicate={handleSelectDuplicate}
                refreshToken={draftRefreshToken}
              />
            )}
          </div>
        </div>
      </div>
      {mobileSidebarOpen && (
        <div
          className="user-manual-mobile-overlay"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <div className="user-manual-body">
        <aside className={`user-manual-sidebar-container ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
          <UserManualSidebar
            sections={sections}
            activeKey={activeKey}
            onSelectArticle={handleArticleSelect}
          />
        </aside>
        <main className="user-manual-content">
          <button
            className="user-manual-mobile-toggle"
            onClick={() => setMobileSidebarOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
            <span>Menu</span>
          </button>
          {duplicateView ? (
            <UserManualDuplicateCompare
              pair={duplicateView}
              onResolved={handleDuplicateResolved}
              onNavigate={handleArticleSelect}
            />
          ) : editingSlug ? (
            <div className="user-manual-content-with-nav">
              <div className="user-manual-content-inner">
                <UserManualArticleEditor
                  slug={editingSlug}
                  onSaved={handleEditSaved}
                  onCancel={() => setEditingSlug(null)}
                />
              </div>
            </div>
          ) : draftView ? (
            <div className="user-manual-content-with-nav">
              <div className="user-manual-content-inner">
                <div className="user-manual-draft-banner">
                  <span>
                    {draftView.publishedNow
                      ? 'Published — live for venues within a minute or two.'
                      : draftView.state === 'revision'
                        ? 'Pending revision — venues still see the previously published version.'
                        : 'Draft — not visible to venues.'}
                  </span>
                  {draftView.article && !draftView.publishedNow && (
                    <Button size="sm" onClick={() => setEditingSlug(draftView.slug)}>Edit</Button>
                  )}
                  {draftView.article && !draftView.publishedNow && (
                    <button
                      type="button"
                      className="user-manual-draft-publish-button"
                      onClick={handlePublishDraft}
                      disabled={draftView.publishing}
                    >
                      {draftView.publishing ? 'Publishing…' : 'Publish'}
                    </button>
                  )}
                </div>
                {draftView.publishError && (
                  <div className="user-manual-draft-banner user-manual-draft-banner-error">
                    Publishing failed. Try again, or publish from Conductor.
                  </div>
                )}
                {draftView.error && (
                  <div className="user-manual-draft-banner user-manual-draft-banner-error">
                    Couldn't load that draft. It may have just changed — reopen it from the Admin menu.
                  </div>
                )}
                {draftView.article && (
                  <UserManualArticle
                    section={{ title: humanizeSlug(draftView.article.section) }}
                    chapter={{ title: humanizeSlug(draftView.article.chapter) }}
                    article={draftView.article}
                    prevArticle={null}
                    nextArticle={null}
                    onNavigate={handleArticleSelect}
                  />
                )}
              </div>
            </div>
          ) : activeEntry ? (
            <div className="user-manual-content-with-nav">
              <div className="user-manual-content-inner">
                {adminDraftsEnabled && (
                  unpublishState?.done ? (
                    <div className="user-manual-draft-banner">
                      <span>Unpublished — venues stop seeing this article within a minute or two. It's now a draft in the Admin menu.</span>
                    </div>
                  ) : (
                    <div className="user-manual-admin-article-bar">
                      {unpublishState?.error && <span className="user-manual-admin-article-bar-error">Unpublish failed. Try again.</span>}
                      <Button size="sm" onClick={() => setEditingSlug(activeEntry.article.slug)}>Edit</Button>
                      <button
                        type="button"
                        className="user-manual-admin-unpublish-button"
                        onClick={handleUnpublishActive}
                        disabled={unpublishState?.working}
                      >
                        {unpublishState?.working ? 'Unpublishing…' : 'Unpublish'}
                      </button>
                    </div>
                  )
                )}
                <UserManualArticle
                  section={activeEntry.section}
                  chapter={activeEntry.chapter}
                  article={activeEntry.article}
                  prevArticle={prevArticle}
                  nextArticle={nextArticle}
                  onNavigate={handleArticleSelect}
                />
              </div>
              <OnPageNav headings={headings} />
            </div>
          ) : (
            <div className="user-manual-empty-state">
              <p>Select an article from the sidebar to get started.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserManualPage;
